import React, { useContext, useMemo, useState } from "react"
import { GlobalStateContext } from "./providers/GlobalStateProvider.jsx"
import { Popover } from "@shopify/polaris"
import { useTranslation } from "react-i18next"
import { Input } from "@mantine/core"
import { getLanguageName, LANGUAGE_CODES } from "../utils/languages.js"
import { useChangeDefaultTranslateLanguage } from "../hooks/useFirestoreUserHooks.js"

export const DefaultTranslateLanguageSelector = () => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { i18n, t } = useTranslation()
	const changeDefaultTranslateLanguage = useChangeDefaultTranslateLanguage()

	const [translatePopoverActive, setTranslatePopoverActive] = useState(false)
	const [languageSearchInput, setLanguageSearchInput] = useState("")

	const _changeDefaultTranslateLanguage = (lang) => {
		dispatch({ defaultTranslateLanguage: lang })
		setTranslatePopoverActive(false)
		changeDefaultTranslateLanguage(state.userId, state.shop, lang)
	}

	const filterLanguageCodes = (languageCodes, searchInput) => {
		if (!searchInput) return languageCodes

		return languageCodes.filter((lang) => {
			return LANGUAGE_CODES[lang]
				.toLowerCase()
				.includes(searchInput.toLowerCase())
		})
	}

	const selectedLanguage = state.defaultTranslateLanguage || ""
	const languageCodes = Object.keys(LANGUAGE_CODES)
	const filteredLanguageCodes = useMemo(
		() => filterLanguageCodes(languageCodes, languageSearchInput),
		[languageCodes, languageSearchInput],
	)

	return (
		<div>
			<div
				style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
			>
				<div
					style={{
						background: "#c2e1ff",
						color: "#154cac",
						borderRadius: "0.3rem",
						display: "flex",
						gap: "0.2rem",
						fontSize: "0.85rem",
						cursor: "pointer",
					}}
					onClick={(e) => setTranslatePopoverActive((prev) => !prev)}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							padding: "0.3rem 0.5rem",
							paddingRight: "0.4rem",
							borderRadius: "0.3rem 0rem 0rem 0.3rem",
							background: "transparent",
						}}
					>
						<svg
							style={{ width: "0.9rem", height: "0.9rem" }}
							viewBox="0 0 32 32"
						>
							<path
								fill="currentColor"
								d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
							></path>
						</svg>
					</div>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "0.2rem",
							padding: "0.3rem 0.5rem",
							paddingLeft: "0rem",
						}}
					>
						<span>
							{t("ai.translate_to")}{" "}
							<span
								style={{
									fontWeight: "600",
									textTransform: "capitalize",
								}}
							>
								{getLanguageName(
									selectedLanguage,
									i18n.language,
								) || t("common.none_selected")}
							</span>
						</span>
						<div
							className="language-selector-icon"
							style={{ height: "0.95rem", width: "0.95rem" }}
						>
							<svg
								style={{ width: "1rem", height: "1rem" }}
								viewBox="0 0 512 512"
							>
								<path
									d="M128 192l128 128 128-128z"
									fill="currentColor"
								></path>
							</svg>
						</div>
					</div>
				</div>

				{selectedLanguage && (
					<div
						style={{
							cursor: "pointer",
							height: "1rem",
							width: "1rem",
							// padding: "0.1rem",
							color: "#d61717",
						}}
						onClick={() => _changeDefaultTranslateLanguage(null)}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="100%"
							height="100%"
							style={{ display: "block" }}
							viewBox="0 0 24 24"
						>
							<path
								fill="currentColor"
								d="M18.36 19.78L12 13.41l-6.36 6.37l-1.42-1.42L10.59 12L4.22 5.64l1.42-1.42L12 10.59l6.36-6.36l1.41 1.41L13.41 12l6.36 6.36z"
							/>
						</svg>
					</div>
				)}
			</div>

			<Popover
				active={translatePopoverActive}
				activator={<div></div>}
				onClose={() => setTranslatePopoverActive(false)}
			>
				<div
					style={{
						padding: "0.5rem",
						height: "12rem",
						width: "9rem",
						overflowY: "scroll",
					}}
				>
					<p style={{ fontSize: "0.7rem", marginBottom: "0.5rem" }}>
						{t("ai.your_store_languages")}
					</p>

					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "0.1rem",
						}}
					>
						{state.availableStoreLanguages.map((lang) => {
							return (
								<div
									key={lang}
									onClick={() =>
										_changeDefaultTranslateLanguage(lang)
									}
									className="language-button"
								>
									<svg
										width="0.85rem"
										height="0.85rem"
										viewBox="0 0 32 32"
									>
										<path
											fill="currentColor"
											d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
										></path>
									</svg>
									<span
										style={{
											fontSize: "0.85rem",
											textTransform: "capitalize",
										}}
									>
										{getLanguageName(lang, i18n.language)}
									</span>
								</div>
							)
						})}
					</div>

					<div
						style={{
							borderTop: "1px solid #ccc",
							paddingTop: "1rem",
							marginTop: "0.5rem",
						}}
					>
						<p
							style={{
								fontSize: "0.7rem",
								marginBottom: "0.5rem",
							}}
						>
							{t("ai.all_languages")}
						</p>

						<Input
							variant="filled"
							size="xs"
							radius="md"
							placeholder={t("ai.search_language")}
							value={languageSearchInput}
							onChange={(e) =>
								setLanguageSearchInput(e.target.value)
							}
						/>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "0.1rem",
								marginTop: "0.5rem",
							}}
						>
							{filteredLanguageCodes.map((lang) => {
								return (
									<div
										key={lang}
										onClick={() =>
											_changeDefaultTranslateLanguage(
												lang,
											)
										}
										className="language-button"
									>
										<svg
											width="0.85rem"
											height="0.85rem"
											viewBox="0 0 32 32"
										>
											<path
												fill="currentColor"
												d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
											></path>
										</svg>
										<span
											style={{
												fontSize: "0.85rem",
												textTransform: "capitalize",
											}}
										>
											{getLanguageName(
												lang,
												i18n.language,
											)}
										</span>
									</div>
								)
							})}
						</div>
					</div>
				</div>
			</Popover>

			<style>{`
				.language-button {
					display: flex;
					gap: 0.3rem;
					align-items: center;
					padding: 0.2rem 0.3rem;
					cursor: pointer;
					background: none;
					border-radius: 0.3rem;
				}
				.language-button:hover {
					background: #e8e8e8;
				}
				.language-selector-icon:hover {
					border-radius: 0.25rem;
					background: #92bde7;
				}
			`}</style>
		</div>
	)
}
